/**
 * Train Sim — signal + barrier logic helpers.
 *
 * Extracted from the (very large) `TrainSimBoard.js` so the new
 * switch-signal / automatic-barrier mechanics live in one focused,
 * test-friendly module instead of growing the board monolith further
 * (see the repo's file-size rule). Everything here is PURE: no Pixi,
 * no DOM, no board state mutation — the board passes in the live
 * data each frame and gets back plain values it can act on.
 *
 * Mechanics these helpers back:
 *   - Trains are stopped by a player-toggled SIGNAL attached to every
 *     switch (red = stop, green = go), gating the switch cell from any
 *     approach direction.
 *   - Level-crossing BARRIERS (sorompó) are car-only + fully
 *     automatic: they drop when a train nears the crossing track cell
 *     and raise after it passes. They never stop a train.
 *   - Trains CRUISE at `TRAIN_CRUISE_FACTOR` and brake toward
 *     `TRAIN_SLOW_FACTOR` only when the next/lookahead switch lamp is
 *     red.
 */

import { TRAINSIM_CONFIG } from './trainsimConfig.js';

/** Random initial signal aspect for a switch at stage start. */
export function randomSignalAspect() {
    return Math.random() < 0.5 ? 'red' : 'green';
}

/**
 * Pick a clean spot to hang a switch's signal lamp so it sits OUT of
 * the way of the rails and stays easy to see + tap.
 *
 * A switch cell has up to 8 ports (N, NE, E, SE, S, SW, W, NW — the
 * connection mask layout shared with `DIR_DELTA`). A T-junction leaves
 * one cardinal free, an L/curve leaves a whole quadrant free, a 4-port
 * cross leaves the diagonals free. We exploit that: hang the lamp in a
 * direction that carries NO track, preferring an open diagonal corner
 * (cleanest — it never overlaps the cardinal rails), then a free
 * cardinal, and finally just above/below as a last resort. Directions
 * that would push the lamp off the board are skipped so it always
 * lands somewhere clickable.
 *
 * Pure: returns a `{ dc, dr }` unit step the board turns into a head
 * offset.
 *
 * The lamp must NEVER look like it is wired to the switch by a "thin
 * line". That artefact happens when the lamp lands in (or pokes into) a
 * neighbouring cell that itself carries a rail — the rail then reads as
 * a connector. So beyond the switch's own free ports we also reject any
 * step whose destination cell is occupied by track or another switch:
 * the lamp is forced to float over genuinely EMPTY ground, with nothing
 * between it and the junction.
 *
 * @param {Array<boolean>|undefined} conn  8-port open/closed mask
 * @param {number} c    switch column
 * @param {number} r    switch row
 * @param {number} cols board width in cells
 * @param {number} rows board height in cells
 * @param {(c:number,r:number)=>boolean} [isOccupied]  true if the cell
 *        carries a rail / switch (so the lamp must not land there)
 * @returns {{dc:number, dr:number}}
 */
export function chooseSignalPlacement(conn, c, r, cols, rows, isOccupied) {
    const C = Array.isArray(conn) ? conn : [];
    const free = (i) => !C[i];
    const inB = (dc, dr) => (c + dc >= 0 && c + dc < cols && r + dr >= 0 && r + dr < rows);
    const occ = typeof isOccupied === 'function' ? isOccupied : () => false;
    // A destination cell is "clear" if it is on-board AND has no rail /
    // switch — i.e. nothing that could draw a line back to the junction.
    const clear = (dc, dr) => inB(dc, dr) && !occ(c + dc, r + dr);

    // Each of the 8 ports: [portIdx, dc, dr, ringNeighbourA, ringNeighbourB].
    // Corners are listed FIRST so they win ties (a diagonal lamp never
    // overlaps the cardinal rails, so it looks cleanest). Ring neighbours
    // are the two adjacent ports in the 8-direction ring.
    const DIRS = [
        [1,  1, -1, 0, 2], // NE (ring N, E)
        [3,  1,  1, 2, 4], // SE (ring E, S)
        [5, -1,  1, 4, 6], // SW (ring S, W)
        [7, -1, -1, 6, 0], // NW (ring W, N)
        [0,  0, -1, 7, 1], // N  (ring NW, NE)
        [2,  1,  0, 1, 3], // E  (ring NE, SE)
        [4,  0,  1, 3, 5], // S  (ring SE, SW)
        [6, -1,  0, 5, 7], // W  (ring SW, NW)
    ];

    // Score each on-board free port:
    //   openness  = 1 (the port itself) + free ring neighbours (0..2)
    //   clearBonus= +10 when the lamp's destination cell is EMPTY ground
    // The clear bonus dominates so a slightly-less-open EMPTY cell always
    // beats a wide-open cell that happens to hold a rail (which would
    // read as a connecting line). Ties fall to DIRS order (corners first).
    let best = null;
    let bestScore = -1;
    for (const [idx, dc, dr, a, b] of DIRS) {
        if (!free(idx) || !inB(dc, dr)) continue;
        const openness = 1 + (free(a) ? 1 : 0) + (free(b) ? 1 : 0);
        const score = openness + (clear(dc, dr) ? 10 : 0);
        if (score > bestScore) {
            bestScore = score;
            best = { dc, dr };
        }
    }
    if (best) return best;

    // No free port is on-board. Prefer ANY direction whose destination
    // cell is clear (even via an occupied port) before sitting on a rail.
    for (const [, dc, dr] of DIRS) {
        if (clear(dc, dr)) return { dc, dr };
    }
    // Then any free port even if it pokes slightly off the board edge.
    for (const [idx, dc, dr] of DIRS) {
        if (free(idx)) return { dc, dr };
    }

    // Fully connected 8-way junction with no free port at all (very
    // rare): hang above, or below for a top-row cell.
    return r === 0 ? { dc: 0, dr: 1 } : { dc: 0, dr: -1 };
}

/**
 * Manhattan distance between two grid cells. Cheap proximity metric —
 * good enough for "is a train near this crossing?" and avoids the
 * sqrt of a true Euclidean measure.
 */
function cellDistance(ac, ar, bc, br) {
    return Math.abs(ac - bc) + Math.abs(ar - br);
}

/**
 * Scan every live train and collect the ids of the BARRIERS that
 * should currently be CLOSED — i.e. a train is on, or within
 * `radius` cells of, the barrier's crossing track cell. The crossing
 * track cell is the barrier (lamp) entity's own cell, which is where
 * the rails physically cross the road.
 *
 * Returns the supplied (or a fresh) Set so callers can reuse one
 * persistent Set across frames and avoid per-frame allocation.
 *
 * @param {Iterable<object>} barriers  lamp/barrier entities (each has c,r,id)
 * @param {Array<object>|null} liveTrains  live train instances
 * @param {number} [radius]
 * @param {Set<string>} [out]
 * @returns {Set<string>} ids of barriers that should be closed
 */
export function computeBarrierClosures(barriers, liveTrains, radius = TRAINSIM_CONFIG.BARRIER_CLOSE_RADIUS_CELLS, out = new Set()) {
    out.clear();
    if (!barriers || !liveTrains || liveTrains.length === 0) return out;
    for (const barrier of barriers) {
        if (!barrier) continue;
        const tc = barrier.c;
        const tr = barrier.r;
        for (const train of liveTrains) {
            if (!train || !train._started || !train._alive || train._reachedGoal) continue;
            const dNow  = cellDistance(train._col, train._row, tc, tr);
            const dNext = cellDistance(train._nextCol, train._nextRow, tc, tr);
            if (Math.min(dNow, dNext) <= radius) {
                out.add(barrier.id);
                break;
            }
        }
    }
    return out;
}

/**
 * Compute the target speed factor for a train given the live board.
 * Cruise on open track; ease toward the slow floor when the next or
 * lookahead cell holds a switch whose signal is RED, scaled by how
 * close the red signal is (closer → slower).
 *
 * Pure: reads only `boardApi.isSwitchSignalRed` + the train's grid
 * cursor + progress. Returns the TARGET; the caller eases the train's
 * live `_speedFactor` toward it for smooth deceleration.
 *
 * @param {object} boardApi  must expose `isSwitchSignalRed(c, r)`
 * @param {object} train     train with `_nextCol/_nextRow/_lookaheadCol/_lookaheadRow/_progress`
 * @returns {number} target speed factor in [SLOW, CRUISE]
 */
export function computeBrakeTargetFactor(boardApi, train) {
    const CRUISE = TRAINSIM_CONFIG.TRAIN_CRUISE_FACTOR;
    const SLOW   = TRAINSIM_CONFIG.TRAIN_SLOW_FACTOR;
    const RADIUS = TRAINSIM_CONFIG.TRAIN_BRAKE_RADIUS_CELLS;
    if (!boardApi || typeof boardApi.isSwitchSignalRed !== 'function') return CRUISE;

    // Distance (in cells) to a red switch ahead. The next cell is
    // `1 - progress` away; the lookahead cell `2 - progress`.
    let dist = Infinity;
    if (boardApi.isSwitchSignalRed(train._nextCol, train._nextRow)) {
        dist = Math.max(0, 1 - train._progress);
    } else if (boardApi.isSwitchSignalRed(train._lookaheadCol, train._lookaheadRow)) {
        dist = Math.max(0, 2 - train._progress);
    }
    if (!Number.isFinite(dist) || dist >= RADIUS) return CRUISE;

    // Linear ramp: right on top of the red switch → SLOW; RADIUS
    // cells out → CRUISE.
    const k = Math.max(0, Math.min(1, dist / RADIUS));
    return SLOW + (CRUISE - SLOW) * k;
}

/**
 * Ease a live speed factor toward a target by at most
 * `TRAIN_BRAKE_EASE_RATE` factor-units per second, so acceleration /
 * deceleration is smooth rather than snapping.
 *
 * @param {number} current
 * @param {number} target
 * @param {number} dtSec
 * @returns {number} the new factor
 */
export function easeSpeedFactor(current, target, dtSec) {
    const rate = TRAINSIM_CONFIG.TRAIN_BRAKE_EASE_RATE * Math.max(0, dtSec);
    const diff = target - current;
    if (Math.abs(diff) <= rate) return target;
    return current + Math.sign(diff) * rate;
}
